(ns practitest.frontend.dashboards-settings.views
  (:require [re-frame.core :as re-frame]
            [reagent.core :as reagent]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.navigation :as navigation]
            [practitest.frontend.components.project-settings-tabs :as project-settings-tabs]
            [practitest.frontend.components.grid.localstorage :as grid-storage]
            [practitest.frontend.components.grid.sessionstorage :as grid-session-storage]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.grid :as grid2]
            [practitest.frontend.components2.grid.action-cells :as action-cells]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.components2.dashboard.dashboard-tab-menu :as dashboard-tab-menu]
            [practitest.frontend.components2.dashboard.dashboard-tab-modal :as dashboard-tab-modal]
            [practitest.frontend.dashboards-settings.events :as events]
            [practitest.frontend.dashboards-settings.subs :as subs]))

(def ^:private default-per-page 10)

(defn- table-id [tab-id]
  (str "dashboard-tab-items-" tab-id))

(defn- distinct-values [items k]
  (vec (sort (distinct (keep k items)))))

(defn- columns [items]
  [{:field         :name_link
    :header        (get-translation "dashboards.grid.item-name" "Dashboard Item Name")
    :type          :internal-url
    :default-width 320
    :sortable?     true
    :autofilter?   true}
   {:field           :chart_type
    :header          (get-translation "dashboards.grid.chart-type" "Chart Type")
    :type            :list
    :multiple?       true
    :possible-values (distinct-values items :chart_type)
    :default-width   160
    :sortable?       true
    :autofilter?     true}
   {:field           :entity_name
    :header          (get-translation "dashboards.grid.entity" "Entity")
    :type            :list
    :multiple?       true
    :possible-values (distinct-values items :entity_name)
    :default-width   140
    :sortable?       true
    :autofilter?     true}
   {:field         :filter_name
    :header        (get-translation "dashboards.grid.filter" "Filter")
    :type          :text
    :default-width 260
    :sortable?     true
    :autofilter?   true}])

(defn- no-permission-tooltip []
  (get-translation "dashboards.no-permission" "You don't have permissions to edit this dashboard tab"))

(defn- action-cell [tab-id row actions-enabled? tab-editable?]
  (let [disabled? (not (and actions-enabled? (:editable row)))
        tooltip   (when-not tab-editable? (no-permission-tooltip))]
    [action-cells/action-cells
     [{:label     (get-translation "generic.button.edit" "Edit")
       :icon      icons/mux-pencil
       :disabled? disabled?
       :tooltip   tooltip
       :on-click  #(set! (.-href js/window.location) (:edit_url row))}
      {:label        (get-translation "generic.button.delete" "Delete")
       :icon         icons/mux-trash
       :disabled?    disabled?
       :tooltip      tooltip
       :on-click     #(re-frame/dispatch [::events/delete-item tab-id (:id row)])
       :confirmation {:title (get-translation "dashboards.confirm.delete-item.title" "Delete Dashboard Item?")
                      :body  (get-translation "dashboards.confirm.delete-item.body"
                                              "This dashboard item will be deleted permanently.")}}]]))

;; The grid renders its drag handle only inside the selection column, which this grid doesn't use,
;; so render the same handle in a dedicated column. Drag enabling is still decided by the grid.
(defn- pre-columns []
  [{:field         :drag-handle
    :header        ""
    :default-width 36
    :width         36
    :type          :custom
    :resizable?    false
    :sortable?     false
    :systemcol?    true
    :cell-fn       (fn [_ {:keys [hover?]} {:keys [has-filters? has-sorting? reorder-in-progress?]}]
                     [:r> grid2/DragHandle
                      #js {:tooltip-text (cond
                                           has-filters? (get-translation "generic.grid.reorder-tooltip.filters-applied"
                                                                         "Filters applied. To reorder, please reset the table using the Reset icon")
                                           has-sorting? (get-translation "generic.grid.reorder-tooltip.sort-applied"
                                                                         "Sorting applied. To reorder, please reset the table using the Reset icon")
                                           :else        (get-translation "generic.grid.reorder-tooltip.drag" "Drag to reorder"))
                           :disabled     (boolean (or has-filters? has-sorting? reorder-in-progress?))
                           :hover        (boolean hover?)}])}])

(defn- post-columns [tab-id actions-enabled? tab-editable?]
  [{:header        (get-translation "generic.label.actions" "Actions")
    :field         :actions
    :default-width 90
    :width         90
    :type          :custom
    :text-align    :center
    :resizable?    false
    :sortable?     false
    :systemcol?    true
    :class         ["action-menu"]
    :cell-fn       (fn [_ row _] [action-cell tab-id row actions-enabled? tab-editable?])}])

(defn- grid-toolbar [{:keys [archived editable can_add_item new_item_url]}]
  [:div.grid-toolbar-right
   [buttons/button
    {:tertiary? true
     :icon      [icons/mux-reg-plus]
     :text      (get-translation "dashboards.add-item" "Add Dashboard Item")
     :disabled? (or archived (not can_add_item))
     :tooltip   (cond
                  (not editable)     (no-permission-tooltip)
                  archived           nil
                  (not can_add_item) (get-translation "dashboards.add-item.limit" "There are up to 8 items on each dashboard."))
     :href      (when (and (not archived) can_add_item) new_item_url)}]])

(defn- tab-grid [_tab]
  (let [grid-state      (reagent/atom {})
        menu-collapsed? (re-frame/subscribe [::navigation/project-level-menu-collapsed?])
        sorting?        (re-frame/subscribe [::subs/field :sorting?])]
    (fn [{:keys [id items archived editable] :as tab}]
      (let [tid              (table-id id)
            actions-enabled? (and editable (not archived))
            rows             (mapv #(assoc % :name_link {:title (:title %) :url (:edit_url %)}) items)]
        [grid2/grid
         {:columns              (cond->> (columns items)
                                  archived (mapv #(assoc % :sortable? false :autofilter? false)))
          :pre-columns          (when actions-enabled? (pre-columns))
          :post-columns         (post-columns id actions-enabled? editable)
          :full-data?           true
          :data                 rows
          :total-count          (count rows)
          :resizable?           true
          :expanded?            @menu-collapsed?
          :on-expand-click      #(re-frame/dispatch [::navigation/set-project-level-menu-collapsed? (not @menu-collapsed?)])
          :table-id             tid
          :grid-state           grid-state
          :local-state-updating grid-state
          :grid-toolbar         (fn [_] [grid-toolbar tab])
          :on-sort-end          (when actions-enabled?
                                  #(re-frame/dispatch [::events/sort-items id %]))
          :reorder-in-progress? @sorting?
          :empty-table-message  (get-translation "dashboards.grid.empty" "No dashboard items in this tab yet")
          :columns-widths-save  (reagent/partial grid-storage/save-column-sizes tid)
          :columns-widths-load  (reagent/partial grid-storage/load-column-sizes tid)
          :sort-order-save      (reagent/partial grid-session-storage/save-sort-order tid)
          :sort-order-load      (if archived
                                  (constantly nil)
                                  (reagent/partial grid-session-storage/load-sort-order tid))
          :per-page-save        (reagent/partial grid-storage/save-per-page tid)
          :per-page-load        #(or (grid-storage/load-per-page tid) default-per-page)
          :current-page-save    (reagent/partial grid-storage/save-current-page tid)
          :current-page-load    (reagent/partial grid-storage/load-current-page tid)}]))))

(defn- tab-header [{:keys [id name items archived private summary_url] :as tab} collapsed? last-unarchived?]
  [:div.dashboards-settings-tab-header
   [:span.dashboards-settings-tab-chevron
    {:role     "button"
     :on-click #(grid-storage/set-section-collapsed :dashboard-tab id (swap! collapsed? not))}
    (if @collapsed? [icons/mux-chevron-right] [icons/mux-chevron-down2])]
   [buttons/button
    {:tertiary? true
     :class     "dashboards-settings-tab-name"
     :text      (str name " (" (count items) ")")
     :disabled? archived
     :href      (when-not archived summary_url)}]
   (when private
     [:span.dashboards-settings-tab-private (get-translation "dashboards.tab.private" "Private")])
   [dashboard-tab-menu/dashboard-tab-menu
    {:tab              tab
     :last-unarchived? last-unarchived?
     :on-edit          (fn []
                         (mixpanel/track "dashboard_tab-edit")
                         (re-frame/dispatch [::events/open-edit-modal id]))
     :on-clone         #(re-frame/dispatch [::events/clone-tab id])
     :on-archive       (fn []
                         (mixpanel/track "Archive Dashboard")
                         (re-frame/dispatch [::events/archive-tab id]))
     :on-unarchive     (fn []
                         (mixpanel/track "Unarchive Dashboard")
                         (re-frame/dispatch [::events/unarchive-tab id]))
     :on-delete        #(re-frame/dispatch [::events/delete-tab id])}]
   (when (and archived (:editable tab))
     [buttons/button
      {:tertiary? true
       :icon      [icons/mux-archive-2]
       :text      (get-translation "dashboards.tab.unarchive" "Unarchive")
       :on-click  (fn []
                    (mixpanel/track "Unarchive Dashboard")
                    (re-frame/dispatch [::events/unarchive-tab id]))}])])

(defn- tab-section [{:keys [id]}]
  (let [collapsed? (reagent/atom (grid-storage/get-section-collapsed :dashboard-tab id false))]
    (fn [tab last-unarchived?]
      [:div.dashboards-settings-tab
       {:class (when (:archived tab) "archived")}
       [tab-header tab collapsed? last-unarchived?]
       (when-not @collapsed?
         ;; Remount on archive/unarchive so sort/filter state resets
         ^{:key (str (:id tab) "-" (:archived tab))}
         [tab-grid tab])])))

(defn- edit-modal []
  (let [tab               @(re-frame/subscribe [::subs/edit-modal-tab])
        {:keys [new? saving? error]} @(re-frame/subscribe [::subs/field :edit-modal])
        new-tab           @(re-frame/subscribe [::subs/field :new-tab])
        tab-filter-fields @(re-frame/subscribe [::subs/field :tab-filter-fields])]
    (cond
      new?
      ^{:key "new-tab"}
      [dashboard-tab-modal/dashboard-tab-modal
       {:open?             true
        :new?              true
        :tab               (select-keys new-tab [:private :private_locked])
        :tab-filter-fields tab-filter-fields
        :saving?           saving?
        :error             error
        :on-save           #(re-frame/dispatch [::events/create-tab %])
        :on-cancel         #(re-frame/dispatch [::events/close-edit-modal])}]

      tab
      ^{:key (:id tab)}
      [dashboard-tab-modal/dashboard-tab-modal
       {:open?             true
        :tab               tab
        :tab-filter-fields tab-filter-fields
        :saving?           saving?
        :error             error
        :on-save           #(re-frame/dispatch [::events/save-tab (:id tab) %])
        :on-cancel         #(re-frame/dispatch [::events/close-edit-modal])
        :on-regenerate-key #(re-frame/dispatch [::events/regenerate-key (:id tab)])}])))

(defn page []
  (let [tabs             @(re-frame/subscribe [::subs/visible-tabs])
        loading?         @(re-frame/subscribe [::subs/field :loading?])
        show-archived?   @(re-frame/subscribe [::subs/field :show-archived?])
        unarchived-count @(re-frame/subscribe [::subs/unarchived-count])
        ;; Keep "Hide" clickable if the last archived tab was unarchived while shown
        no-archived?     (and (zero? @(re-frame/subscribe [::subs/archived-count]))
                              (not show-archived?))
        can-create?      (:can_create @(re-frame/subscribe [::subs/field :new-tab]))]
    [:div.dashboards-settings-page
     [:div.global-fields-sticky-header
      [project-settings-tabs/project-settings-tabs {}]
      [:div.dashboards-settings-title-row
       [:h1.dashboards-settings-title (get-translation "dashboards.title" "Dashboard Tab List")]
       [:div.dashboards-settings-title-actions
        [buttons/button
         {:icon      [icons/mux-archive-2]
          :text      (if show-archived?
                       (get-translation "dashboards.hide-archived" "Hide Archived Dashboards")
                       (get-translation "dashboards.show-archived" "Show Archived Dashboards"))
          :disabled? (or loading? no-archived?)
          :tooltip   (when no-archived?
                       (get-translation "dashboards.no-archived" "There are no archived dashboards"))
          :on-click  #(re-frame/dispatch [::events/toggle-show-archived])}]
        [buttons/button
         {:primary?  true
          :icon      [icons/mux-reg-plus]
          :text      (get-translation "dashboards.add-tab" "Add Dashboard Tab")
          :disabled? (or loading? (not can-create?))
          :tooltip   (when (and (not loading?) (not can-create?))
                       (get-translation "dashboards.add-tab.no-permission" "You don't have permissions to create dashboard tabs"))
          :on-click  #(re-frame/dispatch [::events/open-new-modal])}]]]]
     [:div.dashboards-settings-body
      (if loading?
        [:div.dashboards-settings-loading [spinners/mux-spinner]]
        (for [tab tabs]
          ^{:key (:id tab)}
          [tab-section tab (and (not (:archived tab)) (<= unarchived-count 1))]))]
     [edit-modal]]))
